import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, Slider, ToggleButton, ToggleButtonGroup,
  Button, Collapse, Divider, CircularProgress, Chip, IconButton,
} from '@mui/material';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import SentimentSatisfiedAltIcon       from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentNeutralIcon            from '@mui/icons-material/SentimentNeutral';
import SentimentVeryDissatisfiedIcon   from '@mui/icons-material/SentimentVeryDissatisfied';
import PsychologyIcon  from '@mui/icons-material/Psychology';
import MicIcon         from '@mui/icons-material/Mic';
import MicOffIcon      from '@mui/icons-material/MicOff';
import { deltaToColor } from './sectorColors';
import { recoach }     from '../../utils/raceEngineerApi';
import { useTranslation } from 'react-i18next';

const RATING_ICONS  = { 1: <SentimentSatisfiedAltIcon fontSize="small" />, 2: <SentimentNeutralIcon fontSize="small" />, 3: <SentimentVeryDissatisfiedIcon fontSize="small" /> };
const RATING_COLORS = { 1: 'success', 2: 'default', 3: 'error' };

// ── Hook speech recognition (identico a DriverDashboard) ─────────────────────
function useSpeechRecognition() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript]   = useState('');
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous     = true;
    rec.interimResults = true;
    rec.lang           = 'it-IT';
    rec.onresult = (e) => {
      let text = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) text += e.results[i][0].transcript;
      }
      if (text) setTranscript(p => p + ' ' + text);
    };
    rec.onerror = () => setIsListening(false);
    rec.onend   = () => setIsListening(false);
    recognitionRef.current = rec;
  }, []);

  const startListening = () => { setTranscript(''); setIsListening(true);  recognitionRef.current?.start(); };
  const stopListening  = () => { setIsListening(false); recognitionRef.current?.stop(); };
  return { isListening, transcript, startListening, stopListening, setTranscript };
}

// ── Slider per un singolo settore ────────────────────────────────���────────────
function SectorSliders({ fb, onChange }) {
  const { t } = useTranslation('race_engineer');
  const sliders = [
    { key: 'entry', label: t('feedback.entry'), color: 'primary' },
    { key: 'mid',   label: t('feedback.mid'),   color: 'secondary' },
    { key: 'exit',  label: t('feedback.exit'),  color: 'success' },
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 1.5 }}>
      {sliders.map(({ key, label, color }) => (
        <Box key={key}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            {label} — <strong>{fb[key] ?? 5}</strong>/10
          </Typography>
          <Slider
            value={fb[key] ?? 5}
            onChange={(_, v) => onChange(key, v)}
            min={1} max={10} step={1} marks
            valueLabelDisplay="auto"
            color={color}
            size="small"
            sx={{ mt: 0.25, mb: 0 }}
          />
        </Box>
      ))}
    </Box>
  );
}

// ── Riga singolo settore ──────────────────────────────────────────────────────
function SectorRow({ sector, fb, onUpdate }) {
  const { t } = useTranslation('race_engineer');
  const RATING_LABELS = { 1: t('feedback.rating_ok'), 2: t('feedback.rating_neutral'), 3: t('feedback.rating_problem') };
  const { isListening, transcript, startListening, stopListening, setTranscript } = useSpeechRecognition();
  const delta = sector.delta_vs_ref_s ?? sector.delta_vs_best_s ?? 0;
  const color = deltaToColor(Math.max(0, delta));

  // Append dettato alle note
  useEffect(() => {
    if (!transcript) return;
    onUpdate('note', (fb.note ? fb.note + ' ' : '') + transcript.trim());
    setTranscript('');
  }, [transcript]);

  return (
    <Box>
      {/* Header settore */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: color, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: '#0A0A0A' }}>{sector.sector}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
          {sector.duration_s?.toFixed(3)}s
        </Typography>
        {delta !== 0 && (
          <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
            {delta > 0 ? '+' : ''}{delta.toFixed(3)}s
          </Typography>
        )}

        {/* Sensazione globale */}
        <ToggleButtonGroup
          size="small" exclusive
          value={fb.rating ?? null}
          onChange={(_, v) => v !== null && onUpdate('rating', v)}
          sx={{ ml: 'auto', '& .MuiToggleButton-root': { py: 0.25, px: 0.75, fontSize: 11 } }}
        >
          {[1, 2, 3].map(r => (
            <ToggleButton key={r} value={r} color={RATING_COLORS[r]} sx={{ gap: 0.25 }}>
              {RATING_ICONS[r]}
              <span style={{ fontSize: 10 }}>{RATING_LABELS[r]}</span>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Slider ingresso / apice / uscita */}
      <SectorSliders fb={fb} onChange={(k, v) => onUpdate(k, v)} />

      {/* Note + microfono */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          size="small" fullWidth multiline maxRows={3}
          placeholder={`S${sector.sector} — sovrasterzo, sottosterzo, frenata, trazione…`}
          value={fb.note || ''}
          onChange={e => onUpdate('note', e.target.value)}
          sx={{
            '& .MuiInputBase-root': { fontSize: 12, bgcolor: 'background.default' },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
          }}
        />
        <IconButton
          color={isListening ? 'error' : 'default'}
          onClick={isListening ? stopListening : startListening}
          size="small"
          sx={{
            bgcolor: isListening ? 'rgba(244,67,54,0.12)' : 'action.hover',
            border: '1px solid', borderColor: isListening ? 'error.main' : 'divider',
            width: 36, height: 36, flexShrink: 0, mt: 0.25,
          }}
          title={isListening ? t('feedback.stop_dictation') : t('feedback.start_dictation')}
        >
          {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
        </IconButton>
      </Box>
    </Box>
  );
}

// ── Componente principale ─────────────────────────────────────────────────────
export default function DriverFeedback({ sectors = [], coachingContext, onRecoach }) {
  const { t } = useTranslation('race_engineer');
  const [open, setOpen]       = useState(false);
  const [feedback, setFeedback] = useState({});
  const [loading, setLoading] = useState(false);

  const update = (sectorNum, key, value) =>
    setFeedback(p => ({ ...p, [sectorNum]: { ...p[sectorNum], [key]: value } }));

  const hasFeedback = Object.values(feedback).some(
    f => f?.note || f?.rating || f?.entry !== undefined || f?.mid !== undefined || f?.exit !== undefined
  );

  const handleRecoach = useCallback(async () => {
    if (!coachingContext) return;
    setLoading(true);
    try {
      const feedbackList = Object.entries(feedback)
        .filter(([, f]) => f?.note || f?.rating || f?.entry != null)
        .map(([sector, f]) => ({
          sector: parseInt(sector),
          note:   [
            f.note || '',
            f.entry != null ? `Ingresso ${f.entry}/10` : '',
            f.mid   != null ? `Apice ${f.mid}/10`   : '',
            f.exit  != null ? `Uscita ${f.exit}/10` : '',
          ].filter(Boolean).join(' · '),
          rating: f.rating || null,
        }));

      const result = await recoach({
        sectors:         coachingContext.sectors,
        session_summary: coachingContext.session_summary,
        weather:         coachingContext.weather,
        driver_feedback: feedbackList,
      });
      onRecoach?.(result.coaching);
    } catch (e) {
      console.error('Recoach error', e);
    } finally {
      setLoading(false);
    }
  }, [feedback, coachingContext, onRecoach]);

  if (!sectors.length) return null;

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }} data-noprint="true">

      {/* Header collapsible */}
      <Box onClick={() => setOpen(o => !o)} sx={{
        px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1,
        cursor: 'pointer', bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' }, userSelect: 'none',
      }}>
        <PsychologyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" fontWeight={700}>{t('feedback.title')}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {t('feedback.subtitle')}
        </Typography>
        {hasFeedback && (
          <Chip label={Object.values(feedback).filter(f => f?.note || f?.rating).length}
            size="small" color="primary" sx={{ ml: 0.5, fontSize: 10, height: 18 }} />
        )}
        <ExpandMoreIcon sx={{ ml: 'auto', color: 'text.secondary', fontSize: 20,
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </Box>

      <Collapse in={open}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {sectors.map((s, i) => (
            <Box key={s.sector}>
              <SectorRow
                sector={s}
                fb={feedback[s.sector] || {}}
                onUpdate={(key, value) => update(s.sector, key, value)}
              />
              {i < sectors.length - 1 && <Divider sx={{ my: 1.5, opacity: 0.25 }} />}
            </Box>
          ))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained" color="secondary"
              disabled={!hasFeedback || loading || !coachingContext}
              onClick={handleRecoach}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <PsychologyIcon />}
              sx={{ minWidth: 200 }}
            >
              {loading ? t('feedback.analyzing') : t('feedback.reanalyze')}
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}
